import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './index';

export type MetadataMappingStatus =
  | 'idle'
  | 'loading'
  | 'completedFetchingData'
  | 'waitingForInput'
  | 'error';

export interface MetadataMappingFunnelState {
  status: MetadataMappingStatus;
  error?: string;
  message?: string;
  startedAt?: number;
}

export interface MetadataMappingState {
  funnelMaps: {
    [funnelMapId: string]: MetadataMappingFunnelState;
  };
}

const initialState: MetadataMappingState = {
  funnelMaps: {},
};

export const metadataMappingSlice = createSlice({
  name: 'metadataMapping',
  initialState,
  reducers: {
    setMetadataMappingLoading: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        isLoading: boolean;
      }>,
    ) => {
      const { funnelMapId, isLoading } = action.payload;

      state.funnelMaps[funnelMapId] = {
        status: isLoading ? 'loading' : 'idle',
        error: undefined,
        message: undefined,
        startedAt: isLoading ? Date.now() : undefined,
      };
    },
    setMetadataMappingMessage: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        message: string;
      }>,
    ) => {
      const { funnelMapId, message } = action.payload;
      state.funnelMaps[funnelMapId] = {
        status: 'waitingForInput',
        message,
        error: undefined,
        startedAt: undefined,
      };
    },
    setMetadataMappingCompleted: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;

      state.funnelMaps[funnelMapId] = {
        status: 'completedFetchingData',
        error: undefined,
        message: undefined,
        startedAt: undefined,
      };
    },
    setMetadataMappingError: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        error: string;
      }>,
    ) => {
      const { funnelMapId, error } = action.payload;

      state.funnelMaps[funnelMapId] = {
        status: 'error',
        error,
        message: undefined,
        startedAt: undefined,
      };
    },
    clearMetadataMappingState: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;

      delete state.funnelMaps[funnelMapId];
    },
    cleanupExpiredMetadataMappingStates: (state) => {
      const now = Date.now();
      const TIMEOUT_DURATION = 10 * 60 * 1000;

      Object.keys(state.funnelMaps).forEach((funnelMapId) => {
        const funnelState = state.funnelMaps[funnelMapId];
        if (
          funnelState.status === 'loading' &&
          funnelState.startedAt &&
          now - funnelState.startedAt > TIMEOUT_DURATION
        ) {
          state.funnelMaps[funnelMapId] = {
            status: 'error',
            error: 'Metadata mapping request timed out. Please try again.',
            message: undefined,
            startedAt: undefined,
          };
        }
      });
    },
  },
});

export const {
  setMetadataMappingLoading,
  setMetadataMappingCompleted,
  setMetadataMappingError,
  clearMetadataMappingState,
  cleanupExpiredMetadataMappingStates,
  setMetadataMappingMessage,
} = metadataMappingSlice.actions;

const defaultMetadataMappingState: MetadataMappingFunnelState = {
  status: 'idle',
  error: undefined,
  message: undefined,
  startedAt: undefined,
};

export const selectMetadataMappingState = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId] || defaultMetadataMappingState;

export const selectMetadataMappingStatus = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status || 'idle';

export const selectMetadataMappingLoading = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status === 'loading';

export const selectMetadataMappingCompleted = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status === 'completedFetchingData';

export const selectMetadataMappingMessage = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.message;

export const selectMetadataMappingError = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.error;

export const selectHasAnyMetadataMappingInProgress = () => (state: RootState) => {
  return Object.values(state.metadataMapping.funnelMaps).some(
    (funnelMapState) => funnelMapState.status === 'loading',
  );
};

export default metadataMappingSlice.reducer;
